'use client'

import { useRef } from "react"
import ProductCard from "../product/ProductCard"
import { ArrowUp, Loader2, PackageOpen } from "lucide-react"
import { useInfiniteProducts } from "@/hooks/use-infinite-products"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"
import { Button } from "@/components/elements/Button"
import Skeleton from "@/components/elements/Skeleton"
import EmptyState from "@/components/elements/EmptyState"
import { getAspectRatioClass } from "@/utils/util"

export default function AllProducts({ vendor, title, description, session }: { vendor: any, wishlist?: any, title?: string, description?: string, session?: any }) {
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore, pendingCount } = useInfiniteProducts({
    vendorId: vendor?.id,
    limit: 20,
    initialFilters: {},
    session
  })

  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: loadMore,
    enabled: hasMore && !isLoading && !isLoadingMore,
  })

  const isWideRatio = vendor?.imageAspectRatio === '16/9'
  const gridCols = isWideRatio ? 'lg:grid-cols-4' : 'lg:grid-cols-5'
  const aspectRatioClass = getAspectRatioClass(vendor?.imageAspectRatio)

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="cont py-4 sm:py-8">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex flex-col gap-1">
          <div className="text-foreground text-xl sm:text-3xl leading-6 sm:leading-9 font-semibold tracking-[-0.75%]">{title || 'Бүх бүтээгдэхүүн'}</div>
          {description && <div className="text-muted-foreground font-normal text-sm leading-5 sm:text-lg sm:leading-7 tracking-[.0125em]">{description}</div>}
        </div>
        {!isLoading && (
          <span className="text-sm text-muted-foreground">{metadata.total} бүтээгдэхүүн</span>
        )}
      </div>
      {isLoading ? (
        <div className={`grid grid-cols-2 md:grid-cols-4 ${gridCols} gap-x-4 gap-y-10 mt-4`}>
          {Array.from({ length: 10 }).map((_, index) => (
            <SkeletonProduct key={index} aspectRatioClass={aspectRatioClass} />
          ))}
        </div>
      ) : products.length > 0 ? (
        <>
          <div className={`grid grid-cols-2 md:grid-cols-4 ${gridCols} gap-x-4 gap-y-10 mt-4`}>
            {products.map((product: any) => (
              <div key={product.customId || product.id}>
                <ProductCard product={product} vendor={vendor} />
              </div>
            ))}
            {Array.from({ length: pendingCount }).map((_, i) => (
              <div key={`pending-${i}`} ref={i === 0 ? loadMoreRef : undefined}>
                <SkeletonProduct aspectRatioClass={aspectRatioClass} animate={false} />
              </div>
            ))}
          </div>
          <div ref={pendingCount === 0 ? loadMoreRef : null} className="w-full pt-12 flex justify-center">
            {isLoadingMore && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Ачаалж байна...</span>
              </div>
            )}
            {!hasMore && products.length > 0 && (
              <div className="flex flex-col items-center gap-4">
                <span className="text-sm text-muted-foreground">Бүх бүтээгдэхүүн ачаалагдлаа ({metadata.total})</span>
                <Button onClick={scrollToTop} variant="outline" size="sm" icon={ArrowUp}>Дээш буцах</Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <EmptyState icon={PackageOpen} title="Бүтээгдэхүүн байхгүй байна" description="Яг одоогоор ямар нэгэн бүтээгдэхүүн байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу." />
      )}
    </div>
  )
}

// animate=false for the pending tail — hundreds of pulsing placeholders is its own jank
function SkeletonProduct({ aspectRatioClass, animate = true }: { aspectRatioClass?: string, animate?: boolean }) {
  const defaultAspectRatio = aspectRatioClass || 'aspect-w-1 aspect-h-1'
  const still = animate ? '' : 'animate-none'
  return (
    <span className="group text-sm cursor-pointer">
      <div className={`${defaultAspectRatio} w-full relative overflow-hidden rounded-lg`}>
        <Skeleton className={`absolute inset-0 w-full h-full ${still}`} />
      </div>
      <div className="grid xl:hidden w-full grid-cols-2 gap-2 mt-2 h-7">
        <Skeleton className={`w-full h-full rounded ${still}`} />
        <Skeleton className={`w-full h-full rounded ${still}`} />
      </div>
      <div className="flex flex-col text-center mt-2 space-y-2">
        <Skeleton className={`h-5 w-full rounded ${still}`} />
        <Skeleton className={`h-4 w-3/4 mx-auto rounded ${still}`} />
        <Skeleton className={`h-4 w-1/2 mx-auto rounded ${still}`} />
      </div>
    </span>
  )
}
